import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import DropdownButton from "react-bootstrap/DropdownButton";
import Spinner from "components/Spinner";
import OpenQueryReminderModal from "components/OpenQueryReminderModal";
import { PhGridPagination, PhGridFilter, usePagination, PhGridSort } from "components/PhGrid";
import { remindersEnabled } from "../../flags";
import { cookieAttributes } from "util/resumeCookieSettings";
import { useHistory, useLocation } from "react-router-dom";
import { useOpenQueryBridge } from "features/queryReports/utils/commonFunctions";
import {
  marketShareIDs,
  clinicalMarketShareIDs,
  populationTrackerIDs,
  clinicalVolumesIDs,
  trendedPOSIDs,
  placeOfServiceByCountyIDs,
  placeOfServiceIDs,
  billingReportIDs,
  countyLevelServicesIDs,
  shareOfCareIDs,
  campaignEffectivenessIDs
} from 'features/queryReports/utils/reportIds';
import MarketShare from "../QueryDetails/reports/MarketShare";
import ClinicalMarketShare from "../QueryDetails/reports/ClinicalMarketShare";
import PopulationTracker from "components/QueryDetails/reports/PopulationTracker";
import ClinicalVolumes from "components/QueryDetails/reports/ClinicalVolumes";
import PlaceOfServiceByCounty from "components/QueryDetails/reports/PlaceOfServiceByCounty";
import TrendedPOS from "components/QueryDetails/reports/TrendedPOS";
import PlaceOfService from "components/QueryDetails/reports/PlaceOfService";
import BillingReport from "components/QueryDetails/reports/BillingReport";
import ShareOfCare from "components/QueryDetails/reports/ShareOfCare";
import CampaignEffectiveness from "components/QueryDetails/reports/CampaignEffectiveness";
import CountyLevelServicesReport from "components/QueryDetails/reports/CountyLevelServices";

/** Local dev uses localhost; otherwise use env-provided domain.
 *  If the env var is missing in non-local, we fail fast with example.invalid.
 */

function QueryRow(props) {
  const handleShowResubmitModal = () => {
    props.setModalCategoryId(props["category-id"]);
    props.setModalResubmitData({
      definition: props.definition,
      categoryId: props["category-id"],
      reportId: props.reportId,
      description: props.description,
    });
    props.setShowResubmitModal(true);
  };

  const handleShowReminderModal = () => {
    props.setReminderJob(props.job);
    props.setShowReminderModal(true);
  };

  return (
    <tr key={props.id}>
      <th scope="row">{props.description || ""}</th>
      <td>{props.id}</td>
      <td>{props.report.split(".").join(", ")}</td>
      <td>{props.dateCreated.toLocaleDateString()}</td>
      <td>{props.status}</td>
      <td>
        {props.status === "PROCESSED" ? (
          <ButtonGroup>
            <DropdownButton as={ButtonGroup} title="Actions" id="bg-nested-dropdown">
              <Dropdown.Item eventKey="1" onClick={handleShowResubmitModal}>
                Resubmit Query
              </Dropdown.Item>
              {remindersEnabled && (
                <Dropdown.Item eventKey="2" onClick={handleShowReminderModal}>
                  Schedule Query Reminder
                </Dropdown.Item>
              )}
            </DropdownButton>
          </ButtonGroup>
        ) : null}
      </td>
    </tr>
  );
}

async function getToken() {
  await Auth.currentAuthenticatedUser(); // ensure session exists
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error("error getting token", err);
  }
}

function OpenQueryQueriesContainer() {
  useEffect(() => {
    async function setResumeCookie() {
      const cookieValue = encodeURIComponent(`QUERY - /openquery/commercial`);
      const resumeCookie = Object.assign(cookieAttributes(), { name: 'platform_resume' }, { value: cookieValue });
      await cookieStore.set(resumeCookie);
    }
    setResumeCookie();
  }, []);

  const {
    paginationOffset,
    setPaginationOffset,
    paginationSize,
    setPaginationSize,
    filterString,
    setFilterString,
    sortColumn,
    setSortColumn,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
  } = usePagination();
  const history = useHistory();
  const location = useLocation();
  const [queries, setQueries] = useState(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [modalCategoryId, setModalCategoryId] = useState("");
  const [modalResubmitData, setModalResubmitData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [reminderJob, setReminderJob] = useState(null);
  const bridge = useOpenQueryBridge({ history, getToken });
  const handleCloseResubmitModal = () => setShowResubmitModal(false);
  const handleCloseReminderModal = () => setShowReminderModal(false);
  const showOpenMyQueries = location.pathname !== "/openquery/queries";
  // Auth readiness + in-flight guard
  const [ready, setReady] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setQueries(null);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Fetch queries only when authenticated
  useEffect(() => {
    if (!ready) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const jobs = await API.get("OpenQueryAPI", `/openquery/jobs`);
        if (cancelled) return;
        const shaped = (jobs?.requests || []).map((job) => ({
          ...job,
          dateCreated: new Date(job.dateCreated),
        }));
        setQueries(shaped);
      } catch (err) {
        console.error("error fetching openquery jobs", err);
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);



  const displayList = useMemo(() => {
    if (!queries) return [];
    return queries
      .filter((query) =>
        [
          query.description || "",
          query.id,
          query.report.split(".").join(", "),
          query.dateCreated.toLocaleDateString(),
          query.status,
        ]
          .join("")
          .toLowerCase()
          .includes(filterString.toLowerCase())
      )
      .sort((a, b) => {
        if (!sortColumn) return 0;
        if (sortDirection === "ascending") {
          return sortKey(a) > sortKey(b) ? 1 : sortKey(a) < sortKey(b) ? -1 : 0;
        } else {
          return sortKey(a) > sortKey(b) ? -1 : sortKey(a) < sortKey(b) ? 1 : 0;
        }
      })
      .slice(paginationOffset, paginationOffset + paginationSize);
  }, [queries, paginationOffset, paginationSize, filterString, sortColumn, sortKey, sortDirection]);

    const renderReport = useCallback((categoryId) => {
      try {
        const propsData = {
          bridge: bridge,
          initialParams: modalResubmitData,
          showOpenMyQueries: showOpenMyQueries
        }
        switch (categoryId) {
          case marketShareIDs.categoryId:
            return <MarketShare {...propsData} />
          case clinicalMarketShareIDs.categoryId:
            return <ClinicalMarketShare {...propsData} />
          case populationTrackerIDs.categoryId:
            return <PopulationTracker {...propsData} />
          case clinicalVolumesIDs.categoryId:
            return <ClinicalVolumes {...propsData} />
          case trendedPOSIDs.categoryId:
            return <TrendedPOS {...propsData} />
          case placeOfServiceByCountyIDs.categoryId: 
            return <PlaceOfServiceByCounty  {...propsData} />
          case placeOfServiceIDs.categoryId:
            return <PlaceOfService {...propsData} />
          case billingReportIDs.categoryId:
            return <BillingReport {...propsData} />
          case countyLevelServicesIDs.categoryId:
            return <CountyLevelServicesReport {...propsData} />
          case shareOfCareIDs.categoryId:
            return <ShareOfCare {...propsData} />
          case campaignEffectivenessIDs.categoryId:
            return <CampaignEffectiveness {...propsData} />
          default:
            return <div>Error finding report type. Please try again.</div>
        }
      }
      catch (error) {
        return <div>Error loading report. Please try again.</div>;
      }
    }, [modalResubmitData])

  return (
    <>
      <div className="card">
        <div
          className="card-header text-light font-weight-bold container-fluid"
        >
          <div className="row">
            <div className="col">Queries</div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="row m-0">
            <div className="col"></div>
            <div className="col-auto mt-3">
              {queries && (
                <PhGridFilter
                  filterString={filterString}
                  setFilterString={setFilterString}
                  setPaginationOffset={setPaginationOffset}
                />
              )}
            </div>
          </div>
          <table className="table table-striped table-responsive" style={{ padding: 0 }}>
            <thead>
              <tr>
                <th scope="col">
                  <PhGridSort
                    columnLabel="File Name"
                    columnName={d => d?.description}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Query Id"
                    columnName={d => d?.id}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Report Name"
                    columnName={d => d?.report.split(".").join(", ")}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Created"
                    columnName={d => d?.dateCreated}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Status"
                    columnName={d => d?.status}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn
                    }}
                  />
                </th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {!queries && (
                <tr>
                  <td colSpan="6" className="text-center">
                    <Spinner />
                  </td>
                </tr>
              )}
              {displayList.map(item => (
                <QueryRow
                  {...item}
                  key={item.id}
                  setShowResubmitModal={setShowResubmitModal}
                  setModalCategoryId={setModalCategoryId}
                  setModalResubmitData={setModalResubmitData}
                  setShowReminderModal={setShowReminderModal}
                  job={item}
                  setReminderJob={setReminderJob}
                />
              ))}
            </tbody>
          </table>
          {queries && (
            <div className="row m-0 justify-content-end" style={{ paddingRight: ".75rem" }}>
              <PhGridPagination
                fullList={queries}
                paginationOffset={paginationOffset}
                setPaginationOffset={setPaginationOffset}
                setPaginationSize={setPaginationSize}
                paginationSize={paginationSize}
                filterString={filterString}
              />
            </div>
          )}
        </div>
      </div>

      <Modal show={errorMsg} onHide={() => setErrorMsg("")}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorMsg}</p>
        </Modal.Body>
      </Modal>

      <Modal
        size="lg"
        show={showResubmitModal}
        onHide={handleCloseResubmitModal}
        className="resubmit-query"
      >
        <Modal.Header closeButton>
          <Modal.Title>Resubmit Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                {showResubmitModal &&
                  modalCategoryId &&
                  renderReport(modalCategoryId)}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {remindersEnabled && (
        <OpenQueryReminderModal
          title="Schedule Reminder"
          job={reminderJob}
          showReminderModal={showReminderModal}
          handleCloseReminderModal={handleCloseReminderModal}
        />
      )}
    </>
  );
}

export default OpenQueryQueriesContainer;
